/**
 * Session codes.
 *
 * A code gets read out over proximity chat or typed from a phone screen, so it
 * has to survive being misheard and mistyped. Two rules follow:
 *
 *   1. The generator never emits a letter that looks like a digit, so I, L and
 *      O are excluded. Digits are all kept.
 *   2. The parser is forgiving in exactly that direction: a typed O becomes 0,
 *      a typed I or L becomes 1. Since the generator can never produce those
 *      letters, the mapping is unambiguous.
 *
 * SubCoach uses `Math.random()` here. This uses the CSPRNG instead — the codes
 * are the only thing standing between a session and a stranger walking in.
 */

/** Letters that cannot be confused with a digit. */
const ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ0123456789";

export const ROOM_CODE_LENGTH = 6;

/** Characters a user might type that we can safely rewrite. */
const CONFUSABLES: Record<string, string> = {
  O: "0",
  I: "1",
  L: "1",
};

/**
 * Generate a fresh session code.
 *
 * Uses rejection sampling so every character is uniformly distributed — a
 * plain modulo would bias the first few letters of the alphabet.
 */
export function generateRoomCode(): string {
  const max = Math.floor(256 / ALPHABET.length) * ALPHABET.length;
  let code = "";

  while (code.length < ROOM_CODE_LENGTH) {
    const bytes = new Uint8Array(ROOM_CODE_LENGTH);
    crypto.getRandomValues(bytes);
    for (const byte of bytes) {
      if (code.length >= ROOM_CODE_LENGTH) break;
      if (byte >= max) continue; // Reject to keep the distribution flat.
      code += ALPHABET[byte % ALPHABET.length];
    }
  }

  return code;
}

/**
 * Clean up user input into a candidate code.
 *
 * Strips separators and whitespace, uppercases, rewrites confusable letters,
 * and truncates. Does not guarantee the result is complete — use
 * {@link isValidRoomCode} for that.
 *
 * Path-like input is reduced to its last segment first. Without that, pasting
 * a share link would be silently mangled: the confusable mapping turns the
 * word "JOIN" into "J01N", which is six valid characters and therefore a
 * perfectly plausible — and completely wrong — code.
 */
export function normalizeRoomCode(input: string): string {
  const candidate = lastPathSegment(input);

  let result = "";
  for (const char of candidate.toUpperCase()) {
    const mapped = CONFUSABLES[char] ?? char;
    if (ALPHABET.includes(mapped)) {
      result += mapped;
      if (result.length === ROOM_CODE_LENGTH) break;
    }
  }
  return result;
}

/**
 * Reduce a URL or path to its final segment, leaving anything else untouched.
 * Query strings and fragments are dropped with it.
 */
function lastPathSegment(input: string): string {
  const trimmed = input.trim();
  if (!trimmed.includes("/")) return trimmed;

  const withoutQuery = trimmed.split(/[?#]/)[0] ?? trimmed;
  const segments = withoutQuery.split("/").filter((segment) => segment.length > 0);
  return segments[segments.length - 1] ?? "";
}

export function isValidRoomCode(code: string): boolean {
  if (code.length !== ROOM_CODE_LENGTH) return false;
  for (const char of code) {
    if (!ALPHABET.includes(char)) return false;
  }
  return true;
}

/**
 * Split a code for display, e.g. `KX7M2P` -> `KX7 M2P`. Easier to read aloud
 * and easier to copy off a second screen without losing your place.
 */
export function formatRoomCode(code: string): string {
  if (code.length !== ROOM_CODE_LENGTH) return code;
  return `${code.slice(0, 3)} ${code.slice(3)}`;
}

/**
 * Identity keys derived from a code.
 *
 * The code *is* the document identity — both the local database name and the
 * sync room name come straight from it. SubCoach kept these separate and paid
 * for it with a temp-document copy dance on every join; this avoids that
 * entirely.
 */
export function persistenceKey(code: string): string {
  return `wardogs-session-${code}`;
}

export function syncRoomName(code: string): string {
  return `wardogs-${code}`;
}
